<template>
    <v-card class="preview-container">
        <!-- Sticky Toolbar (Kept flat, no 'app' prop to keep layout stable) -->
        <v-toolbar dark color="primary" class="preview-toolbar" flat>
            <v-btn icon @click="closeDialog" title="Close window">
                <v-icon>close</v-icon>
            </v-btn>
            <v-toolbar-title>{{ selectedFile.name }}</v-toolbar-title>
            
            <v-spacer></v-spacer>

            <!-- Secure download icon automatically hides for PDFs on Desktop to avoid button duplication -->
            <v-btn 
                v-if="!isPdf(selectedFile) || isMobile" 
                icon 
                @click="download" 
                title="Download file"
            >
                <v-icon>cloud_download</v-icon>
            </v-btn>
        </v-toolbar>

        <!-- Scrollable Preview Area -->
        <v-card-text class="preview-body grey darken-2">
            <!-- LOADING INDICATOR -->
            <div v-if="!iframeReady" class="loading-indicator">
                <v-progress-circular indeterminate color="primary" size="40" />
            </div>
            
            <div v-if="iframeLoadFailed" class="mt-10 red--text text-xs-center">
                Document viewer has encountered an issue while loading the document. Please download the file instead by clicking the download icon.<br>
                <v-btn @click="download" title="Download file" color="primary" class="mt-2">
                    <v-icon left>cloud_download</v-icon> Download
                </v-btn>
            </div>

            <!-- PDFs FOR DESKTOP ONLY (Direct URL streaming with native functional toolbars) -->
            <iframe
                v-if="iframeReady && isPdf(selectedFile) && !isMobile"
                :src="selectedFile.url"
                :key="iframeKey"
                class="pdf-frame"
                frameborder="0"
                @load="onIframeLoad"
            ></iframe>

            <!-- PDFs FOR MOBILE ONLY (Renders a seamless, scrollable dark canvas for all pages) -->
            <div v-if="iframeReady && isPdf(selectedFile) && isMobile" class="pdf-mobile-scroll native-dark-viewer">
                <!-- Loop through total pages to stack them dynamically -->
                <vue-pdf 
                    v-for="pageIndex in numPages"
                    :key="'mob_page_' + pageIndex"
                    :src="selectedFile.url" 
                    :page="pageIndex"
                    style="width: 100%; display: block; margin-bottom: 12px;"
                ></vue-pdf>
            </div>

            <!-- Image View (Now encapsulated within a native dark layout skin wrapper) -->
            <div v-if="isImage(selectedFile)" class="image-native-dark-viewer pa-4">
                <img
                    :src="selectedFile.url"
                    class="legacy-image-file"
                />
            </div>

            <!-- Word / Excel / PowerPoint (YOUR EXACT ORIGINAL WORKING ENGINE CODE AND BINDINGS) -->
            <iframe
                v-if="iframeReady && isOfficeDoc(selectedFile)"
                :src="googleViewerUrl(selectedFile.url)"
                :key="iframeKey"
                class="doc-frame"
                frameborder="0"
                @load="onIframeLoad"
            ></iframe>
        </v-card-text>
    </v-card>
</template>

<script>
// Import the Vue 2 legacy-compatible package component layout wrapper
import vuePdf from 'vue-pdf'

export default {
    components: {
        vuePdf
    },
    data() {
        return {
            selectedFile: {},
            iframeKey: 0,
            iframeReady: false,
            retryCount: 0,
            maxRetries: 3,
            iframeLoadFailed: false,
            loadTimer: null,
            numPages: 0 // Will store the total page count dynamically
        };
    },
    beforeDestroy() {
        clearTimeout(this.loadTimer);
    },
    methods: {
        closeDialog() {
            this.$emit("update:dialogPreview", false);
        },
        setParams(file) {
            this.selectedFile = file;
            this.iframeReady = false;
            this.retryCount = 0; 
            this.iframeLoadFailed = false;
            this.numPages = 0;
            this.iframeKey++; 
            clearTimeout(this.loadTimer);

            setTimeout(() => {
                this.iframeReady = true;
                
                // For Mobile PDFs, fetch the total number of pages programmatically to trigger the loop template
                if (this.isPdf(file) && this.isMobile) {
                    var loadingTask = vuePdf.createLoadingTask(file.url);
                    var self = this;
                    loadingTask.promise.then(function(pdf) {
                        self.numPages = pdf.numPages;
                    }).catch(function(err) {
                        console.error('PDF page counting failure:', err);
                        self.iframeLoadFailed = true;
                    });
                }

                if (this.isOfficeDoc(file)) {
                    this.loadTimer = setTimeout(() => {
                        if (!this.iframeLoadFailed) {
                            this.iframeLoadFailed = true;
                        }
                    }, 12000);
                }
            }, 200);
        },
        onIframeLoad() {
            clearTimeout(this.loadTimer);
        },
        onPdfError(err) {
            console.error('PDF manual canvas parsing error tracker:', err);
            this.iframeLoadFailed = true;
        },
        isPdf(file) {
            var type = file && file.type ? file.type.toLowerCase() : '';
            var name = file && file.name ? file.name.toLowerCase() : '';
            return type.indexOf('pdf') !== -1 || name.indexOf('.pdf') !== -1;
        },
        isImage(file) {
            var type = file && file.type ? file.type.toLowerCase() : '';
            return type.indexOf('image') !== -1;
        },
        isWordDoc(file) {
            var type = file && file.type ? file.type.toLowerCase() : '';
            return type.indexOf('worddocument') !== -1;
        },
        isOfficeDoc(file) {
            if (file && file.type) {
                var type = file.type.toLowerCase();
                var name = file.name ? file.name.toLowerCase() : '';
                return (
                    type.indexOf('worddocument') !== -1 ||
                    type.indexOf('excel') !== -1 ||
                    type.indexOf('presentation') !== -1 ||
                    /\.(docx?|xlsx?|pptx?)$/i.test(name)
                );
            }
            return false;
        },
        
        // YOUR EXACT ORIGINAL WORKING ENGINE CODE
        googleViewerUrl(url) {
            var bustCache = Math.random();
            return 'https://docs.google.com/viewer?url=' + encodeURIComponent(url) + '&embedded=true&t=' + bustCache;
        },
        
        // REENGINEERED DOWNLOAD ENGINE: Safely balances Localhost dev environments, Testing boxes, and Production targets
        async download() {
            if (!this.selectedFile || !this.selectedFile.downloadUrl) return;

            // Detect if we are running in a Cross-Origin context (like local dev or test environments)
            var currentHost = window.location.hostname; // e.g., 'test2.sweldonow.ph' or 'localhost'
            
            // Automatically use native background fallback if running on localhost, port 8083, or test servers
            var isCrossOriginEnvironment = 
                currentHost === "localhost" || 
                window.location.port === "8083" || 
                currentHost.indexOf("test") !== -1; // Catches test2.sweldonow.ph, test.sweldonow.ph, etc.

            if (isCrossOriginEnvironment) {
                // CROSS-ORIGIN ENVIRONMENT STRATEGY: Bypasses JS fetch() to fix test/dev CORS failures cleanly
                var link = document.createElement("a");
                link.href = this.selectedFile.downloadUrl;
                link.setAttribute("download", this.selectedFile.name || "download");
                link.target = "_blank"; 
                
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                // PRODUCTION ENVIRONMENT STRATEGY: Your exact original fetch data mapping structure
                try {
                    var response = await fetch(this.selectedFile.downloadUrl, {
                        method: "GET",
                        mode: "cors",
                        headers: {
                            "Origin": window.location.origin
                        }
                    });

                    if (!response.ok) throw new Error("Failed to download file");

                    var blob = await response.blob();
                    var blobUrl = URL.createObjectURL(blob);
                    var link = document.createElement("a");
                    link.href = blobUrl;
                    link.download = this.selectedFile.name;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(blobUrl);
                } catch (error) {
                    alert("Could not download file. Please try again.");
                }
            }
        },
    },
};
</script>

<style scoped>
.preview-container {
    height: 100vh !important;
    display: flex !important;
    flex-direction: column !important;
}
.preview-toolbar {
    flex-shrink: 0;
}
.preview-body {
    overflow: hidden !important; 
    flex: 1 !important;
    padding: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    position: relative;
}

/* NATIVE DARK VIEW SIMULATION FOR MOBILE */
.pdf-mobile-scroll.native-dark-viewer {
    width: 100%;
    height: calc(100vh - 64px);
    overflow-y: auto !important;
    overflow-x: hidden !important;
    -webkit-overflow-scrolling: touch;
    padding: 16px;
    box-sizing: border-box;
    /* Simulated native reader slate gray color parameter */
    /* background-color: #525659 !important;  */
}

/* Force modern styling on inner canvas views */
.pdf-mobile-scroll >>> .vue-pdf-embed,
.pdf-mobile-scroll >>> span,
.pdf-mobile-scroll >>> canvas {
    width: 100% !important;
    height: auto !important;
    display: block !important;
    /* Standard drop shadow decoration mirroring real desktop page layers */
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
}
.pdf-mobile-scroll {
    animation: fadeInSmooth 0.4s ease-in-out;
}
@keyframes fadeInSmooth {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}
.image-file {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 80vh;
    margin: 40px auto 0 auto;
    display: block;
    object-fit: contain;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
.pdf-frame,
.doc-frame {
    width: 100%;
    height: calc(100vh - 64px);
    border: none;
    flex: 1;
    display: block;
    overflow: auto; 
}
.loading-indicator {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 5;
}
/* UNIFIED NATIVE DARK VIEW FOR IMAGES */
.image-native-dark-viewer {
    width: 100%;
    height: calc(100vh - 64px);
    overflow-y: auto !important;
    overflow-x: hidden !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    /* Exactly matches your Chrome/Adobe slate gray background parameter */
    background-color: #525659 !important; 
    box-sizing: border-box;
}
.legacy-image-file {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 80vh;
    display: block;
    object-fit: contain;
    /* Exactly matches your mobile PDF document drop shadow styling */
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
}
</style>

